import { useEffect, useRef, useState } from 'react';

// material-ui
import { SxProps } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import Transitions from 'components/@extended/Transitions';
import { authService } from 'modules/auth/services/authService';

// assets
import DownOutlined from '@ant-design/icons/DownOutlined';

interface EmpresaEmissora {
  id: string;
  nome: string;
  nomeFantasia?: string | null;
  logoBlobUri?: string | null;
  ehEmpresaEmissoraPadrao: boolean;
}

const LS_KEY = 'rocky_empresa_emissora_ativa';
// Símbolo quadrado da marca (o "B" da Boneside) — vale para as empresas próprias.
const MARCA_ICON = `${import.meta.env.BASE_URL}favicon.svg`;
const nomeCurto = (e: EmpresaEmissora) => e.nomeFantasia || e.nome;

// ==============================|| WORKSPACE - LABEL ||============================== //

function Label({ sx }: { sx?: SxProps }) {
  return <Chip label="Padrão" size="small" color="primary" sx={{ height: 20, ...sx }} />;
}

// ==============================|| HEADER CONTENT - EMPRESA EMISSORA (Workspace) ||============================== //

export default function EmpresaEmissoraSelect() {
  const downSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const [empresas, setEmpresas] = useState<EmpresaEmissora[]>([]);
  const [ativaId, setAtivaId] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const lista = await authService.authFetch<EmpresaEmissora[]>('/api/cadastros/empresas-emissoras');
        if (cancel || !lista || lista.length === 0) return;
        const me = await authService.authFetch<{ empresaEmissoraAtivaId?: string | null }>('/api/auth/me');
        if (cancel) return;
        const padrao = lista.find((e) => e.ehEmpresaEmissoraPadrao) ?? lista[0];
        const ativa = me?.empresaEmissoraAtivaId ?? padrao?.id ?? null;
        setEmpresas(lista);
        setAtivaId(ativa);
        if (ativa) localStorage.setItem(LS_KEY, ativa);
      } catch {
        // seletor é opcional — se falhar, não aparece
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  if (empresas.length === 0) return null;

  const ativa = empresas.find((e) => e.id === ativaId) ?? empresas[0];

  const onChange = async (e: EmpresaEmissora) => {
    setAtivaId(e.id);
    localStorage.setItem(LS_KEY, e.id);
    setOpen(false);
    try {
      await authService.authFetch('/api/auth/me/empresa-emissora-ativa', {
        method: 'PUT',
        body: JSON.stringify({ empresaId: e.id })
      });
    } catch {
      // preferência salva localmente mesmo se o PUT falhar
    }
  };

  return (
    <Box>
      <ButtonBase
        disableRipple
        ref={anchorRef}
        tabIndex={0}
        onClick={handleToggle}
        sx={{
          height: 38,
          minWidth: { xs: 60, sm: 200 },
          border: '1px solid',
          borderColor: 'divider',
          px: 1,
          py: 0.5,
          borderRadius: 1,
          justifyContent: 'flex-start',
          gap: 0.75,
          '&:focus-visible': { outline: '2px solid', outlineColor: 'secondary.dark' }
        }}
      >
        <Avatar alt={nomeCurto(ativa)} variant="rounded" src={MARCA_ICON} sx={{ width: 24, height: 24 }} />
        {!downSM && (
          <>
            <Box component="span" sx={{ typography: 'h6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
              {nomeCurto(ativa)}
            </Box>
            {ativa.ehEmpresaEmissoraPadrao && <Label />}
          </>
        )}
        <Box sx={{ color: 'text.secondary', ml: 'auto' }}>
          <DownOutlined style={{ fontSize: 10 }} />
        </Box>
      </ButtonBase>
      <Popper
        placement={downMD ? 'bottom-start' : 'bottom'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? 0 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top-left' : 'top'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: (theme as any).customShadows?.z1, width: 240 })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  {empresas.map((e) => (
                    <MenuItem key={e.id} selected={e.id === ativa.id} onClick={() => onChange(e)} sx={{ gap: 1, px: 1 }}>
                      <Avatar alt={nomeCurto(e)} src={MARCA_ICON} variant="rounded" sx={{ width: 24, height: 24 }} />
                      <Typography noWrap component="span" variant="caption">
                        {nomeCurto(e)}
                      </Typography>
                      {e.ehEmpresaEmissoraPadrao && <Label sx={{ ml: 'auto' }} />}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
